import { env, pipeline, Tensor } from '@huggingface/transformers';
import './styles.css';

const LABELS = ['abusive', 'safe', 'self_harm', 'spam_link', 'spam_text'];
const BASE_URL = import.meta.env.BASE_URL;
const EXAMPLES = {
  safe: [
    'Hey, how are you doing today?',
    "What's your favorite movie?",
    'Nice to meet you!',
  ],
  abusive: [
    "You're such an idiot",
    'I hate you so much',
    'Go to hell you piece of garbage',
  ],
  spam_link: [
    'Visit example.com/promo for more information',
    'Open example.org/offers to view the announcement',
    'Click example.net/info to read the update',
  ],
  spam_text: [
    'BUY NOW BUY NOW BUY NOW',
    'AAAAAAAAAAAAAAAAAAA',
    'FREE FREE FREE CLICK HERE',
  ],
  self_harm: [
    'I am struggling with thoughts of self-harm and need help',
    'I am overwhelmed and need someone to talk to',
    'I am in a mental health crisis and need support',
  ],
};

const elements = {
  input: document.querySelector('#inputText'),
  classify: document.querySelector('#classifyButton'),
  clear: document.querySelector('#clearButton'),
  retry: document.querySelector('#retryButton'),
  loading: document.querySelector('#loading'),
  results: document.querySelector('#results'),
  error: document.querySelector('#errorBox'),
  baselinePrediction: document.querySelector('#baselinePrediction'),
  baselineTime: document.querySelector('#baselineTime'),
  baselineProbs: document.querySelector('#baselineProbs'),
  transformerPrediction: document.querySelector('#transformerPrediction'),
  transformerTime: document.querySelector('#transformerTime'),
  transformerProbs: document.querySelector('#transformerProbs'),
  baselineStatus: document.querySelector('#baselineStatus'),
  transformerStatus: document.querySelector('#transformerStatus'),
  baselineStatusCard: document.querySelector('#baselineStatusCard'),
  transformerStatusCard: document.querySelector('#transformerStatusCard'),
  statusHeading: document.querySelector('#status-heading'),
};

const state = {
  baseline: null,
  transformer: null,
  loading: false,
  ready: false,
};

function assetUrl(path) {
  return new URL(`${BASE_URL}${path}`, document.baseURI).href;
}

function assetPath(path) {
  return `${BASE_URL}${path}`;
}

function setModelStatus(model, status, message) {
  const card = model === 'baseline' ? elements.baselineStatusCard : elements.transformerStatusCard;
  const target = model === 'baseline' ? elements.baselineStatus : elements.transformerStatus;
  card.classList.remove('loading', 'ready', 'failed');
  if (status) card.classList.add(status);
  target.textContent = message;
}

function setError(message) {
  elements.error.textContent = message;
  elements.error.hidden = false;
  elements.retry.hidden = true;
}

function clearError() {
  elements.error.textContent = '';
  elements.error.hidden = true;
}

function setLoading(isLoading) {
  state.loading = isLoading;
  elements.loading.hidden = !isLoading;
  elements.classify.disabled = isLoading || !state.ready;
}

async function fetchJson(url) {
  const response = await fetch(url, { cache: 'force-cache' });
  if (!response.ok) {
    throw new Error(`Could not load ${url} (${response.status})`);
  }
  return response.json();
}

async function fetchFloat64(url, expectedLength, name) {
  const response = await fetch(url, { cache: 'force-cache' });
  if (!response.ok) {
    throw new Error(`Could not load ${name} (${response.status})`);
  }
  const buffer = await response.arrayBuffer();
  if (buffer.byteLength % Float64Array.BYTES_PER_ELEMENT !== 0) {
    throw new Error(`${name} is not a valid Float64 binary asset`);
  }
  const values = new Float64Array(buffer);
  if (values.length !== expectedLength) {
    throw new Error(`${name} has ${values.length} values; expected ${expectedLength}`);
  }
  for (const value of values) {
    if (!Number.isFinite(value)) {
      throw new Error(`${name} contains a non-finite value`);
    }
  }
  return values;
}

function normalizeForSklearn(text) {
  return text.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase();
}

function tokenizeForSklearn(text) {
  const normalized = normalizeForSklearn(text);
  const matches = normalized.match(/[\p{L}\p{N}_]+/gu) || [];
  return matches.filter((token) => Array.from(token).length >= 2);
}

function softmax(logits) {
  const maxLogit = Math.max(...logits);
  const exponentials = logits.map((logit) => Math.exp(logit - maxLogit));
  const total = exponentials.reduce((sum, value) => sum + value, 0);
  return exponentials.map((value) => value / total);
}

async function loadBaselineModel() {
  const root = assetUrl('models/baseline/');
  const manifest = await fetchJson(`${root}manifest.json`);
  if (manifest.format_version !== 1) {
    throw new Error(`Unsupported baseline asset format: ${manifest.format_version}`);
  }
  if (manifest.class_count !== LABELS.length) {
    throw new Error('Baseline label count does not match the application');
  }

  const vocabulary = await fetchJson(`${root}${manifest.vocabulary_file || 'vocabulary.json'}`);
  const idf = await fetchFloat64(
    `${root}${manifest.idf_file || 'idf.f64'}`,
    manifest.feature_count,
    'baseline IDF weights',
  );
  const coefficients = await fetchFloat64(
    `${root}${manifest.coefficients_file || 'coefficients.f64'}`,
    manifest.class_count * manifest.feature_count,
    'baseline coefficients',
  );
  const intercept = await fetchFloat64(
    `${root}${manifest.intercept_file || 'intercept.f64'}`,
    manifest.class_count,
    'baseline intercept',
  );

  return {
    manifest,
    vocabulary,
    idf,
    coefficients,
    intercept,
  };
}

function predictBaseline(model, text) {
  const started = performance.now();
  const tokens = tokenizeForSklearn(text);
  const counts = new Map();

  const addFeature = (feature) => {
    const index = model.vocabulary[feature];
    if (index === undefined) return;
    counts.set(index, (counts.get(index) || 0) + 1);
  };

  for (let index = 0; index < tokens.length; index += 1) {
    addFeature(tokens[index]);
    if (index + 1 < tokens.length) {
      addFeature(`${tokens[index]} ${tokens[index + 1]}`);
    }
  }

  const weighted = [];
  let squaredNorm = 0;
  for (const [featureIndex, count] of counts) {
    const value = count * model.idf[featureIndex];
    weighted.push([featureIndex, value]);
    squaredNorm += value * value;
  }

  const norm = Math.sqrt(squaredNorm);
  const logits = Array.from(model.intercept);
  if (norm > 0) {
    for (const [featureIndex, value] of weighted) {
      const normalizedValue = value / norm;
      for (let classIndex = 0; classIndex < LABELS.length; classIndex += 1) {
        logits[classIndex] +=
          model.coefficients[classIndex * model.manifest.feature_count + featureIndex] * normalizedValue;
      }
    }
  }

  const values = softmax(logits);
  const probabilities = Object.fromEntries(LABELS.map((label, index) => [label, values[index]]));
  const bestIndex = values.indexOf(Math.max(...values));

  return {
    prediction: LABELS[bestIndex],
    probabilities,
    inference_time_ms: Number((performance.now() - started).toFixed(2)),
  };
}

async function loadTransformerModel() {
  env.allowRemoteModels = false;
  env.allowLocalModels = true;
  env.useBrowserCache = true;
  env.backends.onnx.wasm.wasmPaths = {
    mjs: assetUrl('wasm/ort-wasm-simd-threaded.asyncify.mjs'),
    wasm: assetUrl('wasm/ort-wasm-simd-threaded.asyncify.wasm'),
  };
  env.backends.onnx.wasm.numThreads = 1;

  const mapping = await fetchJson(assetUrl('models/transformer/label_mappings.json'));
  const transformerLabels = Object.keys(mapping.id2label)
    .sort((left, right) => Number(left) - Number(right))
    .map((key) => mapping.id2label[key]);
  if (JSON.stringify(transformerLabels) !== JSON.stringify(LABELS)) {
    throw new Error('Transformer label mapping does not match the application labels');
  }

  // Keep the model identifier relative. An absolute http(s) URL is treated as
  // a remote model by Transformers.js even when it points at this same page.
  const classifier = await pipeline('text-classification', assetPath('models/transformer'), {
    device: 'wasm',
    dtype: 'fp32',
  });

  return { classifier, transformerLabels };
}

async function predictTransformer(model, text) {
  const started = performance.now();
  // Build BERT inputs explicitly. Transformers.js truncates an already
  // special-tokenized sequence by slicing its final [SEP] token, whereas the
  // Python tokenizer reserves space for [CLS] and [SEP]. Keeping this step
  // explicit preserves the original max_length=128 behavior.
  const tokenizer = model.classifier.tokenizer;
  const rawIds = Array.from(
    tokenizer.encode(text, { add_special_tokens: false }),
    Number,
  );
  const clsId = tokenizer._tokenizer.token_to_id('[CLS]');
  const sepId = tokenizer.sep_token_id;
  if (!Number.isInteger(clsId) || !Number.isInteger(sepId)) {
    throw new Error('Transformer tokenizer is missing BERT special-token IDs');
  }
  const contentIds = rawIds.slice(0, 128 - 2);
  const inputIds = [clsId, ...contentIds, sepId];
  const attentionMask = inputIds.map(() => 1);
  const tokenTypeIds = inputIds.map(() => 0);
  const asInt64 = (values) =>
    new Tensor('int64', BigInt64Array.from(values, (value) => BigInt(value)), [1, values.length]);
  const inputs = {
    input_ids: asInt64(inputIds),
    attention_mask: asInt64(attentionMask),
    token_type_ids: asInt64(tokenTypeIds),
  };
  const outputs = await model.classifier.model(inputs);
  const logits = Array.from(outputs.logits.data);
  if (logits.length !== LABELS.length) {
    throw new Error(`Transformer returned ${logits.length} logits; expected ${LABELS.length}`);
  }
  const values = softmax(logits);
  const probabilities = Object.fromEntries(LABELS.map((label, index) => [label, values[index]]));

  const prediction = LABELS.reduce((best, label) =>
    probabilities[label] > probabilities[best] ? label : best, LABELS[0]);

  return {
    prediction,
    probabilities,
    inference_time_ms: Number((performance.now() - started).toFixed(2)),
  };
}

function renderProbabilities(probabilities, container) {
  container.replaceChildren();
  Object.entries(probabilities)
    .sort((left, right) => right[1] - left[1])
    .forEach(([label, probability]) => {
      const row = document.createElement('div');
      row.className = 'prob-bar';

      const labelLine = document.createElement('div');
      labelLine.className = 'prob-label';
      const name = document.createElement('span');
      name.className = `prob-label-name label-${label}`;
      name.textContent = label.replaceAll('_', ' ');
      const percentage = document.createElement('span');
      percentage.textContent = `${(probability * 100).toFixed(1)}%`;
      labelLine.append(name, percentage);

      const bar = document.createElement('div');
      bar.className = 'prob-bar-container';
      const fill = document.createElement('div');
      fill.className = 'prob-bar-fill';
      fill.style.width = `${Math.max(0, Math.min(100, probability * 100))}%`;
      fill.setAttribute('aria-label', `${label}: ${(probability * 100).toFixed(1)} percent`);
      bar.append(fill);

      row.append(labelLine, bar);
      container.append(row);
    });
}

function renderResult(result) {
  elements.baselinePrediction.textContent = result.baseline.prediction;
  elements.baselineTime.textContent = `${result.baseline.inference_time_ms} ms in browser`;
  renderProbabilities(result.baseline.probabilities, elements.baselineProbs);

  elements.transformerPrediction.textContent = result.transformer.prediction;
  elements.transformerTime.textContent = `${result.transformer.inference_time_ms} ms in browser`;
  renderProbabilities(result.transformer.probabilities, elements.transformerProbs);
  elements.results.hidden = false;
}

async function classify(text) {
  if (!state.ready || !state.baseline || !state.transformer) {
    throw new Error('Both models are still loading. Please wait a moment and try again.');
  }
  const cleanText = text.trim();
  if (!cleanText) throw new Error('Please enter some text to classify.');

  const baseline = predictBaseline(state.baseline, cleanText);
  const transformer = await predictTransformer(state.transformer, cleanText);
  return { text: cleanText, baseline, transformer };
}

async function handleClassification() {
  clearError();
  try {
    const text = elements.input.value.trim();
    if (!text) throw new Error('Please enter some text to classify.');
    setLoading(true);
    elements.results.hidden = true;
    renderResult(await classify(text));
  } catch (error) {
    setError(error instanceof Error ? error.message : 'Classification failed.');
  } finally {
    setLoading(false);
  }
}

async function initializeModels() {
  state.ready = false;
  state.baseline = null;
  state.transformer = null;
  elements.classify.disabled = true;
  elements.retry.hidden = true;
  clearError();
  setModelStatus('baseline', 'loading', 'Loading vocabulary and weights…');
  setModelStatus('transformer', 'loading', 'Loading tokenizer and FP32 ONNX weights…');
  elements.statusHeading.textContent = 'Loading both models';

  const baselinePromise = loadBaselineModel();
  const transformerPromise = loadTransformerModel();

  const [baselineResult, transformerResult] = await Promise.allSettled([
    baselinePromise,
    transformerPromise,
  ]);

  if (baselineResult.status === 'fulfilled') {
    state.baseline = baselineResult.value;
    setModelStatus('baseline', 'ready', 'Ready for local inference');
  } else {
    setModelStatus('baseline', 'failed', 'Could not load baseline assets');
  }

  if (transformerResult.status === 'fulfilled') {
    state.transformer = transformerResult.value;
    setModelStatus('transformer', 'ready', 'Ready for local inference');
  } else {
    setModelStatus('transformer', 'failed', 'Could not load transformer assets');
  }

  state.ready = Boolean(state.baseline && state.transformer);
  elements.classify.disabled = !state.ready;
  elements.statusHeading.textContent = state.ready ? 'Both models are ready' : 'Model loading needs attention';

  if (!state.ready) {
    const failures = [baselineResult, transformerResult]
      .filter((result) => result.status === 'rejected')
      .map((result) => result.reason?.message || 'Unknown model-loading error');
    setError(`Model loading failed. ${failures.join(' ')}`);
    elements.retry.hidden = false;
  }
}

function setExample(category) {
  const values = EXAMPLES[category] || [];
  elements.input.value = values[0] || '';
  elements.input.focus();
}

function clearInput() {
  elements.input.value = '';
  elements.results.hidden = true;
  clearError();
  elements.input.focus();
}

elements.classify.addEventListener('click', handleClassification);
elements.clear.addEventListener('click', clearInput);
elements.retry.addEventListener('click', initializeModels);
elements.input.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    handleClassification();
  }
});
document.querySelectorAll('[data-example]').forEach((button) => {
  button.addEventListener('click', () => setExample(button.dataset.example));
});

// Expose only the public demo surface for browser parity checks and debugging.
window.textModerationDemo = {
  classify,
  getState: () => ({
    baselineReady: Boolean(state.baseline),
    transformerReady: Boolean(state.transformer),
    ready: state.ready,
  }),
};

initializeModels().catch((error) => {
  setError(error instanceof Error ? error.message : 'Model initialization failed.');
  elements.retry.hidden = false;
});
